'use client';

import { useEffect, useMemo, useState } from 'react';
import { Copy, Download, RotateCcw, FileText, Check } from 'lucide-react';

type Tone = 'friendly' | 'neutral' | 'developer';
type Format = 'markdown_long' | 'markdown_compact' | 'plain';

type Form = {
  version: string;
  date: string;
  headline: string;
  features: string;
  improvements: string;
  fixes: string;
  breaking: string;
  tone: Tone;
  format: Format;
};

const STORAGE_KEY = 'planetroadmap.release-notes-generator.v1';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const EXAMPLE: Form = {
  version: 'v2.4.0',
  date: todayIso(),
  headline:
    'A faster dashboard, smarter notifications, and a long-awaited fix for CSV exports.',
  features: `feat(dashboard): pinned views with drag-to-reorder
feat: weekly digest email summarizing roadmap activity
feat(api): cursor-based pagination on /v1/items`,
  improvements: `perf: dashboard initial load is 2.3x faster
ux: keyboard shortcuts for the most common roadmap actions
docs: add a guide for migrating from /v0 to /v1 of the API`,
  fixes: `fix: CSV export now respects the active filter
fix(notifications): duplicate emails when a project had two owners
fix: dark mode contrast on the changelog widget`,
  breaking: `BREAKING: /v0 of the API is removed (deprecated since 2025-09)
BREAKING: webhook payload field "user" renamed to "actor"`,
  tone: 'friendly',
  format: 'markdown_long',
};

const BLANK: Form = {
  version: '',
  date: todayIso(),
  headline: '',
  features: '',
  improvements: '',
  fixes: '',
  breaking: '',
  tone: 'friendly',
  format: 'markdown_long',
};

const COMMIT_PREFIX_RE = /^(feat|feature|fix|bugfix|chore|refactor|perf|docs|style|test|ci|build|revert)(\([^)]+\))?:\s*/i;
const BREAKING_PREFIX_RE = /^breaking(\s*change)?:\s*/i;

function cleanLine(raw: string): string {
  let s = raw.trim();
  if (!s) return '';
  // strip leading bullet markers
  s = s.replace(/^[-*•]\s+/, '');
  s = s.replace(/^\d+\.\s+/, '');
  // strip conventional-commit prefixes
  s = s.replace(COMMIT_PREFIX_RE, '');
  s = s.replace(BREAKING_PREFIX_RE, '');
  // trim trailing period
  s = s.replace(/\.\s*$/, '');
  // capitalize first letter
  if (s.length > 0) s = s[0].toUpperCase() + s.slice(1);
  return s;
}

function cleanLines(raw: string): string[] {
  return raw
    .split('\n')
    .map(cleanLine)
    .filter((l) => l.length > 0);
}

function intro(tone: Tone, headline: string): string {
  if (!headline.trim()) return '';
  if (tone === 'developer') return headline.trim();
  if (tone === 'neutral') return headline.trim();
  return headline.trim();
}

function buildOutput(form: Form): string {
  const features = cleanLines(form.features);
  const improvements = cleanLines(form.improvements);
  const fixes = cleanLines(form.fixes);
  const breaking = cleanLines(form.breaking);

  const versionTitle = form.version.trim() || 'Release';
  const dateLine = form.date.trim();
  const headlineLine = intro(form.tone, form.headline);

  if (form.format === 'plain') {
    const out: string[] = [];
    out.push(`${versionTitle.toUpperCase()}${dateLine ? ` — ${dateLine}` : ''}`);
    out.push('='.repeat(Math.min(60, versionTitle.length + (dateLine ? dateLine.length + 4 : 0))));
    if (headlineLine) {
      out.push('');
      out.push(headlineLine);
    }
    const sections: { name: string; items: string[] }[] = [
      { name: 'WHAT IS NEW', items: features },
      { name: 'IMPROVEMENTS', items: improvements },
      { name: 'BUG FIXES', items: fixes },
      { name: 'BREAKING CHANGES', items: breaking },
    ];
    for (const s of sections) {
      if (s.items.length === 0) continue;
      out.push('');
      out.push(s.name);
      out.push('-'.repeat(s.name.length));
      for (const item of s.items) out.push(`- ${item}`);
    }
    return out.join('\n');
  }

  if (form.format === 'markdown_compact') {
    const out: string[] = [];
    out.push(`# ${versionTitle}${dateLine ? ` — ${dateLine}` : ''}`);
    if (headlineLine) {
      out.push('');
      out.push(headlineLine);
    }
    out.push('');
    out.push('## Changes');
    const groups: { label: string; items: string[] }[] = [
      { label: 'New', items: features },
      { label: 'Improved', items: improvements },
      { label: 'Fixed', items: fixes },
      { label: 'Breaking', items: breaking },
    ];
    for (const g of groups) {
      for (const item of g.items) {
        out.push(`- **${g.label}:** ${item}`);
      }
    }
    return out.join('\n');
  }

  // markdown_long
  const out: string[] = [];
  out.push(`# ${versionTitle}${dateLine ? ` — ${dateLine}` : ''}`);
  if (headlineLine) {
    out.push('');
    out.push(headlineLine);
  }
  const sections: { heading: string; items: string[] }[] = [
    { heading: "What's new", items: features },
    { heading: 'Improvements', items: improvements },
    { heading: 'Bug fixes', items: fixes },
    { heading: 'Breaking changes', items: breaking },
  ];
  for (const s of sections) {
    if (s.items.length === 0) continue;
    out.push('');
    out.push(`## ${s.heading}`);
    for (const item of s.items) out.push(`- ${item}`);
  }
  return out.join('\n');
}

export function ReleaseNotesGenerator() {
  const [form, setForm] = useState<Form>(BLANK);
  const [hydrated, setHydrated] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Form;
        if (parsed && typeof parsed === 'object') {
          setForm({ ...BLANK, ...parsed });
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setForm(EXAMPLE);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [form, hydrated]);

  const output = useMemo(() => buildOutput(form), [form]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((curr) => ({ ...curr, [key]: value }));
  }

  function loadExample() {
    setForm(EXAMPLE);
  }

  function clear() {
    if (!confirm('Clear all fields?')) return;
    setForm({ ...BLANK, date: todayIso() });
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  function downloadFile() {
    const isPlain = form.format === 'plain';
    const ext = isPlain ? 'txt' : 'md';
    const type = isPlain ? 'text/plain' : 'text/markdown';
    const blob = new Blob([output], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (form.version || 'release').toLowerCase().replace(/[^a-z0-9.]+/g, '-').replace(/^-|-$/g, '');
    a.download = `${slug || 'release'}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading generator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">
                Version / title
              </label>
              <input
                type="text"
                value={form.version}
                onChange={(e) => update('version', e.target.value)}
                placeholder="v2.4.0"
                className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => update('date', e.target.value)}
                className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">
              Highlight headline
            </label>
            <input
              type="text"
              value={form.headline}
              onChange={(e) => update('headline', e.target.value)}
              placeholder="One sentence summarizing the release"
              className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <FieldArea
            label="New features"
            hint="One per line — feat: prefixes are stripped automatically"
            value={form.features}
            onChange={(v) => update('features', v)}
          />
          <FieldArea
            label="Improvements"
            hint="One per line"
            value={form.improvements}
            onChange={(v) => update('improvements', v)}
          />
          <FieldArea
            label="Bug fixes"
            hint="One per line — fix: prefixes are stripped automatically"
            value={form.fixes}
            onChange={(v) => update('fixes', v)}
          />
          <FieldArea
            label="Breaking changes"
            hint="One per line — these are highlighted in the output"
            value={form.breaking}
            onChange={(v) => update('breaking', v)}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Tone</label>
              <select
                value={form.tone}
                onChange={(e) => update('tone', e.target.value as Tone)}
                className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="friendly">Friendly</option>
                <option value="neutral">Neutral / corporate</option>
                <option value="developer">Developer / technical</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Format</label>
              <select
                value={form.format}
                onChange={(e) => update('format', e.target.value as Format)}
                className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="markdown_long">Markdown (long)</option>
                <option value="markdown_compact">Markdown (compact)</option>
                <option value="plain">Plain text</option>
              </select>
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-6 lg:self-start space-y-3">
          <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
            <div className="border-b border-border/60 bg-muted/30 px-4 py-2 text-xs font-semibold text-muted-foreground flex items-center justify-between">
              <span>Preview</span>
              <span className="font-mono">
                {form.format === 'plain' ? '.txt' : '.md'}
              </span>
            </div>
            <pre className="px-4 py-4 text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-[70vh] overflow-y-auto">
              {output}
            </pre>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copy}
              className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={downloadFile}
              className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={loadExample}
              className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Load example
            </button>
            <button
              onClick={clear}
              className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Clear
            </button>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground text-center pt-2">
        Your draft is saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}

function FieldArea({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-muted-foreground mb-1">
        {label} <span className="font-normal text-muted-foreground/70">— {hint}</span>
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="w-full bg-transparent border border-border/60 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary resize-y"
      />
    </div>
  );
}
